import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { TradeShowPlan } from '../../constants/trade-shows';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * A tenant's plan for one show (PD-17632). A row with `directoryShowId` is a
 * show from the Workd-wide directory: its dates, place and prices are read live
 * from `workd_mission_control.ts_edition`, so an organizer's change shows up on
 * its own. Its name, dates and city are also kept here as a copy, refreshed on
 * read, because the task queries that show "linked to" run on the tenant
 * database and can't join across to the directory. A row without one is the
 * tenant's own event, private to it, and these columns are the record.
 */
@Entity('trade_show')
@Index('idx_trade_show_directory', ['directoryShowId'])
export class TradeShowEntity extends BaseEntity {
	/** `ts_edition.id`. A plain column: the table is in another database. */
	@Column('int', { name: 'directoryShowId', nullable: true, unsigned: true })
	public directoryShowId?: number | null;

	@Column('varchar', { name: 'name', nullable: true, length: 255 })
	public name?: string | null;

	@Column('date', { name: 'startsOn', nullable: true })
	public startsOn?: string | null;

	@Column('date', { name: 'endsOn', nullable: true })
	public endsOn?: string | null;

	@Column('varchar', { name: 'venue', nullable: true, length: 255 })
	public venue?: string | null;

	@Column('varchar', { name: 'city', nullable: true, length: 120 })
	public city?: string | null;

	@Column('varchar', { name: 'state', nullable: true, length: 60 })
	public state?: string | null;

	@Column('decimal', { name: 'latitude', nullable: true, precision: 10, scale: 7 })
	public latitude?: string | null;

	@Column('decimal', { name: 'longitude', nullable: true, precision: 10, scale: 7 })
	public longitude?: string | null;

	@Column('varchar', { name: 'plan', length: 20, default: 'interested' })
	public plan!: TradeShowPlan;

	@Column('boolean', { name: 'isBooked', default: 0 })
	public isBooked!: boolean;

	@Column('varchar', { name: 'booth', nullable: true, length: 60 })
	public booth?: string | null;

	@Column('varchar', { name: 'boothSize', nullable: true, length: 30 })
	public boothSize?: string | null;

	@Column('text', { name: 'offer', nullable: true })
	public offer?: string | null;

	@Column('int', { name: 'goalBoothMeetings', nullable: true, unsigned: true })
	public goalBoothMeetings?: number | null;

	@Column('text', { name: 'notes', nullable: true })
	public notes?: string | null;

	/** Who runs this show; checklist to-dos go to this person by default. */
	@Column('int', { name: 'fk_user__id_show_owner', nullable: true })
	public showOwnerUserId?: number | null;

	@ManyToOne(() => UserEntity, { createForeignKeyConstraints: false })
	@JoinColumn({ name: 'fk_user__id_show_owner' })
	public showOwner?: UserEntity | null;
}
